import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Loader from '@amzn/meridian/loader';

import VolumeConfiguration from './VolumeConfiguration';
// Classic renders these on the page; New renders the same components inside the Inputs modal.
import AutomationConfiguration from './AutomationConfiguration';
import AttendanceOverride from './AttendanceOverride';
import RateDistribution from './RateDistribution';
import VolumeDistribution from './VolumeDistribution';
import SupportHeadcount from './SupportHeadcount';
import ResultsView from './ResultsView';
import Alert from '@amzn/meridian/alert';
import Modal from '@amzn/meridian/modal';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import PlanStatusAlert, { planHasRun } from './PlanStatusAlert';
import MathematicalOptimizerModal from './MathematicalOptimizerModal';
import FlowPlannerSettingsModal from './FlowPlannerSettingsModal';
import TimeDistributions from './TimeDistributions';
import usePlanPolling from './usePlanPolling';
import { createFlowPlan, approvePlan, rejectPlan, postOpportunities } from './flowPlannerApi';
import useFlowPlannerActuals from './useFlowPlannerActuals';
import useStationAutomationConfig from './useStationAutomationConfig';
import useStationPlanningWindows from './useStationPlanningWindows';
import { OVERRIDE_KEYS, ERROR_MESSAGES } from './flowPlannerConstants';
import { DEFAULT_MULTIPLIER } from './FlowPlannerGridConfig';
import { TEXTS, PLANS, PLAN_STATUS, INTRA_DAY_PLAN_TYPE } from '../../utils/constants';
import {
  initializeModel,
  getCycleTotalVolume,
  computeAbsoluteVolumes,
  computeEndedIntervals
} from './FlowPlannerModel';
import { getOptimizerPresets } from '../../utils/adminSettingsUtil';
import useRiskEvaluationPolling from './useRiskEvaluationPolling';
import ActiveRisksContainer from './ActiveRisksContainer';
import { RISK_ENGINE_PLAN_VERSIONS, isRiskScanning } from './riskEngineModel';
import {
  publishFlowPlannerCountMetric,
  publishFlowPlannerTimeMetric,
  FLOW_PLANNER_METRICS as M
} from '../../utils/metricUtil';
import './shared/flowPlannerShared.css';

const OVERRIDE_FIELDS = [
  'totalVolume',
  'volumeDistributions',
  'rateDistributions',
  'automationConfig',
  'cycleAutomationConfig',
  'supportHeadcountOverrides',
  'attendanceOverrides'
];
const OVERRIDE_METRIC_MAP = {
  totalVolume: M.OVERRIDE_VOLUME,
  volumeDistributions: M.OVERRIDE_VOLUME_DISTRIBUTION,
  rateDistributions: M.OVERRIDE_RATE_MULTIPLIER,
  automationConfig: M.OVERRIDE_AUTOMATION_CONFIG,
  cycleAutomationConfig: M.OVERRIDE_AUTOMATION_CONFIG,
  supportHeadcountOverrides: M.OVERRIDE_SUPPORT_HEADCOUNT,
  attendanceOverrides: M.OVERRIDE_ATTENDANCE
};

function snapshotOverrides(overrides) {
  const snapshot = {};
  OVERRIDE_FIELDS.forEach((key) => {
    snapshot[key] = JSON.stringify(overrides[key]);
  });
  return snapshot;
}

function emitOverrideMetrics(currentOverrides, initialSnapshot) {
  OVERRIDE_FIELDS.forEach((key) => {
    if (JSON.stringify(currentOverrides[key]) !== initialSnapshot[key]) {
      publishFlowPlannerCountMetric(OVERRIDE_METRIC_MAP[key]);
    }
  });
}
const FlowPlanner = ({
  date,
  responseDataForGivenPlan,
  planIdForGivenPlan,
  isOptimizerEnabled = false,
  isExperimentationMode = false,
  adminSettings = null,
  gridLayout = 'new'
}) => {
  const [model, setModel] = useState(null);
  const [planStatus, setPlanStatus] = useState(null);
  const [planId, setPlanId] = useState('');
  const [planVersion, setPlanVersion] = useState('');
  const [error, setError] = useState(null);
  const [isOptimizerOpen, setIsOptimizerOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsSnapshot, setSettingsSnapshot] = useState(null);
  const [isTimeDistributionsOpen, setIsTimeDistributionsOpen] = useState(false);
  const [timeDistributionsFromSettings, setTimeDistributionsFromSettings] = useState(false);
  const [initialVolumeDistributions, setInitialVolumeDistributions] = useState(null);
  const [isVolDistDirty, setIsVolDistDirty] = useState(false);
  const [isPosted, setIsPosted] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [postResult, setPostResult] = useState(null);
  const initialOverridesRef = React.useRef(null);

  const actuals = useFlowPlannerActuals(date, planId);

  // Only consulted before a plan exists; with a plan, automation is a plan override.
  const stationAutomation = useStationAutomationConfig(!planHasRun(planStatus));
  const planningWindows = useStationPlanningWindows();

  const stationOptimizerPresets = useMemo(() => getOptimizerPresets(adminSettings), [adminSettings]);

  // A freshly generated plan is the new baseline, so the volume-distribution override
  // state is rebased with it. Polling installs the model directly, bypassing the
  // initialization effect that would otherwise do this.
  const installPlanModel = useCallback((next) => {
    setModel(next);
    setInitialVolumeDistributions(JSON.stringify(next?.overrides?.volumeDistributions));
    setIsVolDistDirty(false);
  }, []);

  const startPolling = usePlanPolling(responseDataForGivenPlan, setPlanStatus, installPlanModel, setError);

  useEffect(() => {
    const v3DataStr = responseDataForGivenPlan?.[PLANS.FLOW_PLANNER];

    if (!v3DataStr) {
      setModel(null);
      setPlanStatus(null);
      setPlanId('');
      return;
    }

    try {
      const planData = JSON.parse(v3DataStr);

      if (
        planData.status === PLAN_STATUS.PENDING_CREATION ||
        planData.status === PLAN_STATUS.RECEIVED_USER_INPUT ||
        planData.status === PLAN_STATUS.PENDING_USER_INPUT
      ) {
        setPlanStatus(PLAN_STATUS.PENDING_CREATION);
        setPlanId(planData.planId || '');
        setModel(null);
        startPolling(planData.planId);
        return;
      }

      const initialized = initializeModel(v3DataStr);
      setModel(initialized);
      setInitialVolumeDistributions(JSON.stringify(initialized.overrides.volumeDistributions));
      setIsVolDistDirty(false);
      initialOverridesRef.current = snapshotOverrides(initialized.overrides);
      // Drop a stale error so it cannot mask a newly loaded plan's status. Guarded on
      // FAILED because polling writes into responseDataForGivenPlan and re-runs this
      // effect, which would otherwise wipe a genuine creation failure.
      if (planData.status !== PLAN_STATUS.FAILED) setError(null);
      setPlanStatus(planData.status || null);
      setPlanId(planData.planId || planIdForGivenPlan?.[PLANS.FLOW_PLANNER] || '');
      setPlanVersion(planData.planVersion || '');
      setIsPosted(planData.status === PLAN_STATUS.APPROVED && Boolean(planData.isAutoPost));
    } catch (e) {
      publishFlowPlannerCountMetric(M.INIT_MODEL_FAILED);
      console.error('Flow Planner — Failed to initialize model:', e);
      setError(e.message);
    }
  }, [date, responseDataForGivenPlan, planIdForGivenPlan, startPolling]);

  // Keep planVersion in sync — responseDataForGivenPlan is a mutable ref so the
  // main useEffect may not re-run after polling updates it.
  useEffect(() => {
    if (!model) return;
    try {
      const dataStr = responseDataForGivenPlan?.[PLANS.FLOW_PLANNER];
      if (dataStr) {
        const parsed = JSON.parse(dataStr);
        setPlanVersion(parsed.planVersion || '');
        setPlanId(parsed.planId || '');
      }
    } catch (e) {
      // ignore parse errors
    }
  }, [model]);

  const updateSimpleOverride = useCallback((key, value) => {
    setModel((prev) => ({ ...prev, overrides: { ...prev.overrides, [key]: value } }));
  }, []);

  const handleTotalVolumeChange = useCallback(
    (v) => updateSimpleOverride(OVERRIDE_KEYS.TOTAL_VOLUME, v),
    [updateSimpleOverride]
  );
  const handleAutomationConfigChange = useCallback(
    (v) => updateSimpleOverride(OVERRIDE_KEYS.AUTOMATION_CONFIG, v),
    [updateSimpleOverride]
  );
  const handleCycleAutomationConfigChange = useCallback(
    (v) => updateSimpleOverride(OVERRIDE_KEYS.CYCLE_AUTOMATION_CONFIG, v),
    [updateSimpleOverride]
  );

  const handleDistributionsChange = useCallback((cycleName, process, newPctsArray) => {
    setIsVolDistDirty(true);
    setModel((prev) => {
      const cycleVol = prev.overrides.totalVolume.find((v) => v.name === cycleName);
      const total = cycleVol ? getCycleTotalVolume(cycleVol) : 0;
      return {
        ...prev,
        overrides: {
          ...prev.overrides,
          volumeDistributions: {
            ...prev.overrides.volumeDistributions,
            [cycleName]: {
              ...prev.overrides.volumeDistributions[cycleName],
              [process]: { percentages: newPctsArray, volumes: computeAbsoluteVolumes(newPctsArray, total) }
            }
          }
        }
      };
    });
  }, []);

  const handleMultipliersChange = useCallback((cycle, processName, newMultipliers) => {
    setModel((prev) => {
      const processRate = (prev.inputs.processRates[cycle] || []).find((p) => p.name === processName);
      const targetRate = processRate?.targetRate || 0;
      return {
        ...prev,
        overrides: {
          ...prev.overrides,
          rateDistributions: {
            ...prev.overrides.rateDistributions,
            [cycle]: {
              ...prev.overrides.rateDistributions[cycle],
              [processName]: {
                ...prev.overrides.rateDistributions[cycle]?.[processName],
                percentages: newMultipliers,
                rates: newMultipliers.map((m) => Math.round(targetRate * (parseFloat(m) || 1)))
              }
            }
          }
        }
      };
    });
  }, []);

  const handleTargetRateChange = useCallback((cycle, processName, newRate) => {
    setModel((prev) => {
      const updatedRates = (prev.inputs.processRates[cycle] || []).map((p) =>
        p.name === processName ? { ...p, targetRate: newRate } : p
      );
      const currentMult =
        prev.overrides.rateDistributions[cycle]?.[processName]?.percentages ||
        Array(prev.inputs.intervals.length).fill(DEFAULT_MULTIPLIER);
      return {
        ...prev,
        inputs: {
          ...prev.inputs,
          processRates: { ...prev.inputs.processRates, [cycle]: updatedRates }
        },
        overrides: {
          ...prev.overrides,
          rateDistributions: {
            ...prev.overrides.rateDistributions,
            [cycle]: {
              ...prev.overrides.rateDistributions[cycle],
              [processName]: {
                percentages: currentMult,
                rates: currentMult.map((m) => Math.round(newRate * (parseFloat(m) || 1))),
                targetRate: newRate
              }
            }
          }
        }
      };
    });
  }, []);

  const handleSupportHeadcountChange = useCallback((cycle, processName, newHeadcounts) => {
    setModel((prev) => ({
      ...prev,
      overrides: {
        ...prev.overrides,
        supportHeadcountOverrides: {
          ...prev.overrides.supportHeadcountOverrides,
          [cycle]: {
            ...prev.overrides.supportHeadcountOverrides?.[cycle],
            [processName]: newHeadcounts
          }
        }
      }
    }));
  }, []);

  const handleAttendanceChange = useCallback((intervalIndex, value) => {
    setModel((prev) => ({
      ...prev,
      overrides: {
        ...prev.overrides,
        attendanceOverrides: {
          ...prev.overrides.attendanceOverrides,
          [intervalIndex]: value
        }
      }
    }));
  }, []);

  const createPlan = useCallback(
    async (planType, metric) => {
      const startTime = Date.now();
      publishFlowPlannerCountMetric(metric);

      if (initialOverridesRef.current && model) {
        emitOverrideMetrics(model.overrides, initialOverridesRef.current);
        initialOverridesRef.current = snapshotOverrides(model.overrides);
      }

      setPlanStatus(PLAN_STATUS.PENDING_CREATION);
      setPlanId('');
      setPlanVersion('');
      setError(null);
      try {
        const res = await createFlowPlan(
          date,
          { ...model?.overrides, supportHeadcounts: model?.inputs?.supportHeadcounts },
          model?.rawInput,
          planId,
          planType
        );
        publishFlowPlannerTimeMetric(M.CREATE_PLAN, startTime);
        if (!res?.planId) {
          publishFlowPlannerCountMetric(M.CREATE_PLAN_FAILED);
          setPlanStatus(PLAN_STATUS.FAILED);
          setError(ERROR_MESSAGES.CREATE_PLAN);
          return;
        }
        setPlanId(res.planId);
        planIdForGivenPlan[PLANS.FLOW_PLANNER] = res.planId;
        startPolling(res.planId, { fast: true, startTime });
      } catch (e) {
        publishFlowPlannerCountMetric(M.CREATE_PLAN_FAILED);
        setPlanStatus(PLAN_STATUS.FAILED);
        setError(e.message || ERROR_MESSAGES.CREATE_PLAN);
      }
    },
    [model, date, planId, planIdForGivenPlan, startPolling]
  );

  const handleRunPlan = useCallback(() => createPlan(PLANS.PRE_SHIFT, M.CALCULATE_PLAN), [createPlan]);

  const handleUpdatePlan = useCallback(() => createPlan(INTRA_DAY_PLAN_TYPE, M.UPDATE_PLAN), [createPlan]);

  const handleReplan = useCallback(async () => {
    const startTime = Date.now();
    publishFlowPlannerCountMetric(M.REPLAN);
    setPlanStatus(PLAN_STATUS.PENDING_CREATION);
    setPlanId('');
    setPlanVersion('');
    setError(null);
    try {
      const res = await createFlowPlan(date);
      publishFlowPlannerTimeMetric(M.REPLAN, startTime);
      if (!res?.planId) {
        publishFlowPlannerCountMetric(M.CREATE_PLAN_FAILED);
        setPlanStatus(PLAN_STATUS.FAILED);
        setError(ERROR_MESSAGES.CREATE_PLAN);
        return;
      }
      setPlanId(res.planId);
      planIdForGivenPlan[PLANS.FLOW_PLANNER] = res.planId;
      startPolling(res.planId, { startTime, e2eMetric: M.REPLAN_E2E });
    } catch (e) {
      publishFlowPlannerCountMetric(M.CREATE_PLAN_FAILED);
      setPlanStatus(PLAN_STATUS.FAILED);
      setError(e.message || ERROR_MESSAGES.CREATE_PLAN);
    }
  }, [date, planIdForGivenPlan, startPolling]);

  const handleApprovePlan = useCallback(async () => {
    if (!planId) return;
    const startTime = Date.now();
    // Clear a previous failure so a successful retry is not masked by a stale error.
    setError(null);
    try {
      await approvePlan(planId);
      publishFlowPlannerTimeMetric(M.APPROVE_PLAN, startTime);
      publishFlowPlannerCountMetric(M.APPROVE_PLAN);
      setPlanStatus(PLAN_STATUS.APPROVED);
    } catch (e) {
      publishFlowPlannerCountMetric(M.APPROVE_PLAN_FAILED);
      setError(e.message || ERROR_MESSAGES.APPROVE_PLAN);
    }
  }, [planId]);

  // Classic-only: New drops Reject.
  const handleRejectPlan = useCallback(async () => {
    if (!planId) return;
    const startTime = Date.now();
    // Clear a previous failure so a successful retry is not masked by a stale error.
    setError(null);
    try {
      await rejectPlan(planId);
      publishFlowPlannerTimeMetric(M.REJECT_PLAN, startTime);
      publishFlowPlannerCountMetric(M.REJECT_PLAN);
      setPlanStatus(PLAN_STATUS.REJECTED);
    } catch (e) {
      publishFlowPlannerCountMetric(M.REJECT_PLAN_FAILED);
      setError(e.message || ERROR_MESSAGES.REJECT_PLAN);
    }
  }, [planId]);

  const handlePostOpportunities = useCallback(async () => {
    if (!planId) return;
    setIsPosting(true);
    setPostResult(null);
    try {
      await postOpportunities(planId);
      setIsPosted(true);
      setPostResult({ success: true, message: 'Opportunities posted successfully' });
    } catch (e) {
      const raw = e?.errorMessage || e?.message || 'Failed to post opportunities';
      let message;
      try {
        message = JSON.parse(raw).message;
      } catch {
        message = raw;
      }
      setPostResult({ success: false, message });
    } finally {
      setIsPosting(false);
    }
  }, [planId]);

  const handleOptimizerUpdatePlan = useCallback(
    (optimizedPlanStr) => {
      try {
        responseDataForGivenPlan[PLANS.FLOW_PLANNER] = optimizedPlanStr;
        const planData = JSON.parse(optimizedPlanStr);
        const initialized = initializeModel(optimizedPlanStr);
        setModel(initialized);
        setInitialVolumeDistributions(JSON.stringify(initialized.overrides.volumeDistributions));
        setIsVolDistDirty(false);
        setPlanStatus(planData.status || null);
        setPlanId(planData.planId || '');
        setPlanVersion(planData.planVersion || '');
        setIsPosted(planData.status === PLAN_STATUS.APPROVED && Boolean(planData.isAutoPost));
        planIdForGivenPlan[PLANS.FLOW_PLANNER] = planData.planId || '';
      } catch (e) {
        console.error('Flow Planner — Failed to load optimized plan:', e);
        setError(e.message);
      }
    },
    [responseDataForGivenPlan, planIdForGivenPlan]
  );

  const isCreating = planStatus === PLAN_STATUS.PENDING_CREATION;
  const isPendingApproval =
    planStatus === PLAN_STATUS.PENDING_APPROVAL || planStatus === PLAN_STATUS.PENDING_USER_INPUT;

  const isApproved = planStatus === PLAN_STATUS.APPROVED;
  const isManualPostEnabled = Boolean(adminSettings?.manualPostEnabled);
  const isIntraDayEnabled = adminSettings?.intraDayEnabled === true;

  // Poll risk engine while pending approval or after approval (for post button).
  // Hook stops on terminal status (COMPLETED/FAILED) and resets on planId change.
  const shouldPollRiskEngine =
    (isPendingApproval || (isApproved && isManualPostEnabled)) && RISK_ENGINE_PLAN_VERSIONS.has(planVersion);
  const { riskEvaluation } = useRiskEvaluationPolling(planId, shouldPollRiskEngine);

  const isVolDistOverridden = isVolDistDirty;
  const isOpsClockEnded = useMemo(() => {
    const { nodeZoneId, opsClockEndTime } = model?.inputs || {};
    if (!nodeZoneId || !opsClockEndTime) return false;
    const now = new Date();
    const stationNow = new Date(now.toLocaleString('en-US', { timeZone: nodeZoneId }));
    const endTime = new Date(opsClockEndTime);
    return stationNow > endTime;
  }, [model]);
  const isWithinOpsClock = useMemo(() => {
    const { nodeZoneId, opsClockStartTime, opsClockEndTime } = model?.inputs || {};
    if (!nodeZoneId || !opsClockStartTime || !opsClockEndTime) return false;
    const stationNow = new Date(new Date().toLocaleString('en-US', { timeZone: nodeZoneId }));
    return stationNow > new Date(opsClockStartTime) && stationNow < new Date(opsClockEndTime);
  }, [model]);

  const canOpenOptimizer = isOptimizerEnabled && !isOpsClockEnded;

  // Per-interval "window ended" flags (station-local), recomputed on each poll so
  // the strike-through advances with the clock while actuals are shown.
  const endedByInterval = useMemo(
    () => computeEndedIntervals(model?.inputs?.intervals, model?.inputs?.nodeZoneId),
    [model, actuals.response, actuals.refreshing]
  );

  const handleRevertVolumeDistributions = useCallback(() => {
    if (!initialVolumeDistributions) return;
    setModel((prev) => ({
      ...prev,
      overrides: { ...prev.overrides, volumeDistributions: JSON.parse(initialVolumeDistributions) }
    }));
    setIsVolDistDirty(false);
  }, [initialVolumeDistributions]);

  // Cancel restores this snapshot. `inputs` is captured too because a target-rate
  // edit also writes inputs.processRates.
  // Always reachable: pre-plan this modal is the only route to the planning windows
  // editor, so a station with neither file yet still needs it to set them up.
  const handleOpenSettings = useCallback(() => {
    setSettingsSnapshot(model ? { overrides: model.overrides, inputs: model.inputs } : null);
    setIsSettingsOpen(true);
  }, [model]);

  // A plan can finish creating while the modal is open; snapshot then so Cancel restores.
  useEffect(() => {
    if (isSettingsOpen && model && !settingsSnapshot) {
      setSettingsSnapshot({ overrides: model.overrides, inputs: model.inputs });
    }
  }, [isSettingsOpen, model, settingsSnapshot]);

  const handleCancelSettings = useCallback(() => {
    if (settingsSnapshot) {
      setModel((prev) =>
        prev ? { ...prev, overrides: settingsSnapshot.overrides, inputs: settingsSnapshot.inputs } : prev
      );
    } else {
      stationAutomation.reset();
    }
    setSettingsSnapshot(null);
    setIsSettingsOpen(false);
  }, [settingsSnapshot, stationAutomation]);

  // With a plan, Apply recalculates. Without one it saves the station config and
  // keeps the modal open on failure.
  const handleApplySettings = useCallback(async () => {
    if (!model) {
      const saved = await stationAutomation.save();
      if (saved) setIsSettingsOpen(false);
      return;
    }
    setSettingsSnapshot(null);
    setIsSettingsOpen(false);
    handleRunPlan();
  }, [model, stationAutomation, handleRunPlan]);

  const handleSettingsAutomationChange = useCallback(
    (v) => (model ? handleAutomationConfigChange(v) : stationAutomation.setConfig(v)),
    [model, handleAutomationConfigChange, stationAutomation]
  );

  // Opened from the Inputs modal, which closes rather than stacking. The snapshot is
  // kept so pending override edits survive the round trip.
  // Launched from inside Inputs (New) or standalone from the status bar (Classic);
  // only the first should reopen Inputs on close.
  const handleOpenTimeDistributions = useCallback((fromSettings = true) => {
    setIsSettingsOpen(false);
    setTimeDistributionsFromSettings(fromSettings);
    setIsTimeDistributionsOpen(true);
  }, []);

  // Only a full replan re-reads timeDistributions.json; Calculate Plan would carry the
  // old windows forward in rawInput.
  const handleTimeDistributionsSaved = useCallback(() => {
    setSettingsSnapshot(null);
    setIsTimeDistributionsOpen(false);
    planningWindows.reload();
    // Only regenerate an existing plan, and never while one is already being created.
    if (planHasRun(planStatus) && !isCreating) handleReplan();
  }, [planningWindows, handleReplan, planStatus, isCreating]);

  const timeDistributionsButton =
    gridLayout === 'classic' ? (
      <Button type="secondary" size="small" onClick={() => handleOpenTimeDistributions(false)}>
        Time Distributions
      </Button>
    ) : null;

  const timeDistributionsModal = (
    <TimeDistributions
      isOpen={isTimeDistributionsOpen}
      onClose={() => {
        // Back to Inputs only when it was opened from there, so pending edits stay
        // under its Apply/Cancel. Classic launches this standalone.
        setIsTimeDistributionsOpen(false);
        if (timeDistributionsFromSettings) setIsSettingsOpen(true);
      }}
      onSaved={handleTimeDistributionsSaved}
      willReplan={planHasRun(planStatus) && !isCreating}
    />
  );

  const distribution = model
    ? {
        totalVolume: model.overrides.totalVolume,
        distributions: model.overrides.volumeDistributions,
        processEvents: model.inputs.processEvents,
        onChange: handleDistributionsChange,
        isOverridden: !!isVolDistOverridden,
        onRevert: handleRevertVolumeDistributions
      }
    : null;

  const settingsModal = (
    <FlowPlannerSettingsModal
      isOpen={isSettingsOpen}
      onCancel={handleCancelSettings}
      onApply={handleApplySettings}
      model={model}
      planVersion={planVersion}
      stationAutomation={stationAutomation}
      planningWindows={planningWindows}
      onOpenTimeDistributions={handleOpenTimeDistributions}
      onAutomationConfigChange={handleSettingsAutomationChange}
      onCycleAutomationConfigChange={handleCycleAutomationConfigChange}
      onAttendanceChange={handleAttendanceChange}
      onMultipliersChange={handleMultipliersChange}
      onTargetRateChange={handleTargetRateChange}
      gridLayout={gridLayout}
    />
  );

  if (!model || isCreating) {
    return (
      <Column spacingInset="400" alignmentVertical="top" width="100%">
        <PlanStatusAlert
          planStatus={planStatus}
          error={error}
          planId={planId}
          planVersion={planVersion}
          onRunPlan={handleRunPlan}
          onReplan={handleReplan}
          onOpenOptimizer={
            canOpenOptimizer
              ? () => {
                  publishFlowPlannerCountMetric(M.OPEN_OPTIMIZER);
                  setIsOptimizerOpen(true);
                }
              : null
          }
          onOpenSettings={handleOpenSettings}
          onCalculatePlan={handleRunPlan}
          onApprovePlan={null}
          timeDistributions={timeDistributionsButton}
          layout={gridLayout}
          isExperimentationMode={isExperimentationMode}
        />
        {isCreating && (
          <Row alignmentHorizontal="center" spacingInset="600">
            <Loader size="large" />
          </Row>
        )}
        <MathematicalOptimizerModal
          isOpen={isOptimizerOpen}
          onClose={() => setIsOptimizerOpen(false)}
          date={date}
          model={model}
          dependentPlanId={planId}
          optimizerPresets={stationOptimizerPresets}
          onUpdatePlan={handleOptimizerUpdatePlan}
        />
        {settingsModal}
        {timeDistributionsModal}
      </Column>
    );
  }

  return (
    <Column spacingInset="400" alignmentVertical="top" alignmentHorizontal="stretch" width="100%">
      <PlanStatusAlert
        planStatus={planStatus}
        error={error}
        planId={planId}
        planVersion={planVersion}
        onRunPlan={handleRunPlan}
        onReplan={handleReplan}
        onOpenOptimizer={
          canOpenOptimizer
            ? () => {
                publishFlowPlannerCountMetric(M.OPEN_OPTIMIZER);
                setIsOptimizerOpen(true);
              }
            : null
        }
        onOpenSettings={handleOpenSettings}
        onCalculatePlan={handleRunPlan}
        onUpdatePlan={isIntraDayEnabled && isWithinOpsClock ? handleUpdatePlan : null}
        onApprovePlan={isPendingApproval ? handleApprovePlan : null}
        timeDistributions={timeDistributionsButton}
        layout={gridLayout}
        isExperimentationMode={isExperimentationMode}
      />

      <Box type="fill" spacingInset="400" style={{ width: '100%' }}>
        <Column spacing="400" alignmentHorizontal="stretch" width="100%">
          <Text type={TEXTS.H2}>Labor Planning Inputs</Text>
          <Text type={TEXTS.T1} color="secondary">
            Configure volume and volume distribution preferences
          </Text>

          <div className="fp-results-panel">
            <div className="fp-results-section">
              <VolumeConfiguration
                totalVolume={model.overrides.totalVolume}
                onTotalVolumeChange={handleTotalVolumeChange}
                joined
              />
            </div>
            {gridLayout === 'classic' &&
              (model.overrides.automationConfig ||
                (model.overrides.isCycleLevelAutomation && model.overrides.cycleAutomationConfig)) && (
                <div className="fp-results-section">
                  <AutomationConfiguration
                    automationConfig={model.overrides.automationConfig}
                    setAutomationConfig={handleSettingsAutomationChange}
                    availableCycles={model.inputs.availableCycles}
                    cycleAutomationConfig={model.overrides.cycleAutomationConfig}
                    isCycleLevelAutomation={model.overrides.isCycleLevelAutomation}
                    onCycleAutomationConfigChange={handleCycleAutomationConfigChange}
                    joined
                  />
                </div>
              )}
            {gridLayout === 'classic' && (
              <div className="fp-results-section">
                <AttendanceOverride
                  laborAvailability={model.inputs.laborAvailability}
                  intervals={model.inputs.intervals}
                  attendanceOverrides={model.overrides.attendanceOverrides || {}}
                  onAttendanceChange={handleAttendanceChange}
                  readOnly={planVersion === 'DYNAMIC_V2'}
                  joined
                />
              </div>
            )}
            {gridLayout === 'classic' && (
              <div className="fp-results-section">
                <RateDistribution
                  availableCycles={model.inputs.availableCycles}
                  intervals={model.inputs.intervals}
                  processRates={model.inputs.processRates}
                  rateDistributions={model.overrides.rateDistributions}
                  packageSizeByInterval={model.inputs.packageSizeByInterval}
                  onMultipliersChange={handleMultipliersChange}
                  onTargetRateChange={handleTargetRateChange}
                  joined
                />
              </div>
            )}
            {gridLayout === 'classic' && (
              <div className="fp-results-section">
                <SupportHeadcount
                  availableCycles={model.inputs.availableCycles}
                  intervals={model.inputs.intervals}
                  supportHeadcounts={model.inputs.supportHeadcounts}
                  overrides={model.overrides.supportHeadcountOverrides || {}}
                  onChange={handleSupportHeadcountChange}
                  joined
                />
              </div>
            )}
            {gridLayout === 'classic' && (
              <div className="fp-results-section">
                <VolumeDistribution
                  intervals={model.inputs.intervals}
                  availableCycles={model.inputs.availableCycles}
                  totalVolume={model.overrides.totalVolume}
                  volumeDistributions={model.overrides.volumeDistributions}
                  onDistributionsChange={handleDistributionsChange}
                  processEvents={model.inputs.processEvents}
                  isOverridden={!!isVolDistOverridden}
                  onRevert={handleRevertVolumeDistributions}
                  adherenceResponse={actuals.response}
                  showActuals={actuals.showActuals}
                  endedByInterval={endedByInterval}
                  joined
                />
              </div>
            )}
          </div>
          {/* New moves these into the sticky status bar; Classic keeps the original
              button row at the foot of the inputs panel. */}
          {gridLayout === 'classic' && (
            <Box spacingInsetTop="400">
              <Column spacing="400" alignmentHorizontal="stretch" width="100%">
                <Row spacing="300" alignmentHorizontal="end" width="100%">
                  {isOptimizerEnabled && (
                    <Button
                      type="secondary"
                      size="large"
                      onClick={() => {
                        publishFlowPlannerCountMetric(M.OPEN_OPTIMIZER);
                        setIsOptimizerOpen(true);
                      }}
                      disabled={isVolDistOverridden || isOpsClockEnded}>
                      Optimizer
                    </Button>
                  )}
                  <Button type="primary" size="large" onClick={handleRunPlan}>
                    Calculate Plan
                  </Button>
                  {isPendingApproval && (
                    <React.Fragment>
                      <Button type="primary" size="large" onClick={handleApprovePlan}>
                        Approve
                      </Button>
                      <Button type="tertiary" size="large" onClick={handleRejectPlan}>
                        Reject
                      </Button>
                    </React.Fragment>
                  )}
                  {(isPendingApproval || isApproved) && isManualPostEnabled && (
                    <Button
                      type="primary"
                      size="large"
                      disabled={!isApproved || isPosted || isRiskScanning(riskEvaluation)}
                      onClick={() => {
                        setPostResult(null);
                        setIsPostModalOpen(true);
                      }}>
                      {isPosted ? 'Posted' : 'Post Opportunities'}
                    </Button>
                  )}
                </Row>
              </Column>
            </Box>
          )}
        </Column>
      </Box>

      <Modal
        title="Post Opportunities"
        open={isPostModalOpen}
        onClose={() => setIsPostModalOpen(false)}
        closeLabel="Close"
        width="900px">
        <Column spacing="300">
          <Text>The following opportunities will be posted to LSS:</Text>
          <Table headerRows={1} showDividers spacing="small">
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Headcount</TableCell>
              <TableCell>Start</TableCell>
              <TableCell>End</TableCell>
              <TableCell>Workgroups</TableCell>
            </TableRow>
            {(model?.outputs?.laborOpportunities || []).map((opp, idx) => (
              <TableRow key={idx}>
                <TableCell>{opp.opportunityType}</TableCell>
                <TableCell>{opp.headcount || 0}</TableCell>
                <TableCell>
                  <span style={{ whiteSpace: 'nowrap' }}>{opp.applicability?.timeWindow?.start || '—'}</span>
                </TableCell>
                <TableCell>
                  <span style={{ whiteSpace: 'nowrap' }}>{opp.applicability?.timeWindow?.end || '—'}</span>
                </TableCell>
                <TableCell>{(opp.workgroupTags || []).join(', ')}</TableCell>
              </TableRow>
            ))}
          </Table>
          {postResult && (
            <Alert type={postResult.success ? 'success' : 'error'} size="medium">
              {postResult.message}
            </Alert>
          )}
          {!postResult && (
            <Text type="b200">
              <em>Clicking Post will publish opportunities to LSS immediately.</em>
            </Text>
          )}
          <Row alignmentHorizontal="end" spacing="200">
            {!postResult && (
              <>
                <Button type="secondary" onClick={() => setIsPostModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="primary" disabled={isPosting} onClick={handlePostOpportunities}>
                  {isPosting ? 'Posting...' : 'Post'}
                </Button>
              </>
            )}
            {postResult && (
              <Button type="primary" onClick={() => setIsPostModalOpen(false)}>
                Close
              </Button>
            )}
          </Row>
        </Column>
      </Modal>

      <ResultsView
        optimizerAction={
          isOptimizerEnabled && gridLayout !== 'classic' ? (
            <Button
              type="secondary"
              size="small"
              onClick={() => {
                publishFlowPlannerCountMetric(M.OPEN_OPTIMIZER);
                setIsOptimizerOpen(true);
              }}
              disabled={isOpsClockEnded}>
              Optimizer
            </Button>
          ) : null
        }
        gridData={model.outputs?.gridData || null}
        laborSummary={model.outputs?.laborSummary || null}
        availableCycles={model.inputs.availableCycles}
        intervals={model.inputs.intervals}
        laborAvailabilityChart={model.outputs?.laborAvailabilityChart || null}
        requiredHCPerInterval={model.outputs?.requiredHCPerInterval || []}
        availableHCPerInterval={model.outputs?.availableHCPerInterval || []}
        paidHoursPerInterval={model.outputs?.paidHoursPerInterval || []}
        opportunityHCPerInterval={model.outputs?.opportunityHCPerInterval || null}
        volumeWIPChart={model.outputs?.volumeWIPChart || null}
        laborOpportunities={model.outputs?.laborOpportunities || []}
        planVersion={planVersion}
        adherenceResponse={actuals.response}
        endedByInterval={endedByInterval}
        showActuals={actuals.showActuals}
        adherenceLoading={actuals.loading}
        adherenceRefreshing={actuals.refreshing}
        adherenceError={actuals.error}
        onToggleActuals={actuals.toggle}
        onDismissAdherenceError={actuals.dismissError}
        gridLayout={gridLayout}
        distribution={distribution}
        activeRisks={shouldPollRiskEngine ? <ActiveRisksContainer riskEvaluation={riskEvaluation} /> : null}
        supportHeadcount={
          gridLayout === 'classic'
            ? null
            : {
                supportHeadcounts: model.inputs.supportHeadcounts,
                overrides: model.overrides.supportHeadcountOverrides || {},
                onChange: handleSupportHeadcountChange
              }
        }
      />

      <MathematicalOptimizerModal
        isOpen={isOptimizerOpen}
        onClose={() => setIsOptimizerOpen(false)}
        date={date}
        model={model}
        dependentPlanId={planId}
        optimizerPresets={stationOptimizerPresets}
        onUpdatePlan={handleOptimizerUpdatePlan}
      />
      {settingsModal}
      {timeDistributionsModal}
    </Column>
  );
};

export default FlowPlanner;
